import { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import React from 'react';

export interface Validator {
  name: string;
  message: string;
  test: (value: string) => boolean | Promise<boolean>;
}

export interface ChildrenParams {
  valid: boolean;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLoad: (value: string | number | readonly string[] | undefined) => void;
  testing: boolean;
  errors: string[];
  tested: boolean;
}

export interface ValidatorParams {
  value: string | number;
}

interface Props {
  children: (childrenParams: ChildrenParams) => React.ReactNode;
  // validate: (validatorParams: ValidatorParams) => Promise<boolean>;
  validators: Validator[];

  // Will call validators before field is blurred.
  // Useful for immediate feedback.
  alwaysValidateOnChange: boolean;

  // Set this to false when you want to turn debouncing of testing off.
  debounceTesting: boolean;

  debounceTime: number;

  onTest: (isValid: boolean, value: string) => void;

  defaultValue: string | null;
}

interface State {
  focused: boolean;
  valid: boolean;
  inputAdded: boolean;
  testing: boolean;
  tested: boolean;
  errors: string[];
}

class Validate extends React.Component<Props, State> {
  static defaultProps = {
    alwaysValidateOnChange: false,
    debounceTesting: true,
    debounceTime: 500,
    onTest: noop,
    defaultValue: null,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      focused: false,
      valid: false,
      inputAdded: false,
      testing: false,
      tested: false,
      errors: [],
    };

    this.debouncedTestInput = debounce(this.testInput, props.debounceTime);
  }

  componentWillUnmount(): void {
    this.unmounted = true;
  }

  debouncedTestInput: DebouncedFunc<(value: string) => Promise<void>>;

  unmounted = false;

  onFocus = (): void => {
    if (this.unmounted) {
      return;
    }

    this.setState({
      focused: true,
      tested: false,
      errors: [],
    });
  };

  testInput = async (value: string): Promise<void> => {
    if (this.unmounted) {
      return;
    }

    const { validators, onTest } = this.props;

    const stuff = validators.map(async (v) => {
      let result = v.test(value);

      if (typeof result !== 'boolean') {
        result = await result;
      }

      return {
        ...v,
        result,
      };
    });

    this.setState({
      focused: false,
      inputAdded: true,
      testing: true,
    });

    const validatorsWithResults = await Promise.all(stuff);

    const valid = validatorsWithResults.every((v) => v.result === true);
    const errors = validatorsWithResults
      .filter((v) => v.result === false)
      .map((v) => v.message);

    this.setState({
      testing: false,
      tested: true,
      valid,
      errors,
    });

    onTest(valid, value);
  };

  onBlur = async (evt: React.FocusEvent<HTMLInputElement>): Promise<void> => {
    if (this.unmounted) {
      return;
    }

    if (this.props.debounceTesting) {
      this.debouncedTestInput(evt.target.value);
    } else {
      this.testInput(evt.target.value);
    }
  };

  onChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
    const { debounceTesting, alwaysValidateOnChange } = this.props;
    const { inputAdded } = this.state;

    if (inputAdded || alwaysValidateOnChange) {
      if (debounceTesting) {
        this.debouncedTestInput(evt.target.value);
      } else {
        this.testInput(evt.target.value);
      }
    }
  };

  onLoad = (value: string | number | readonly string[] | undefined): void => {
    const { debounceTesting } = this.props;

    if (isString(value)) {
      if (debounceTesting) {
        this.debouncedTestInput(value);
      } else {
        this.testInput(value);
      }
    }
  };

  render(): React.ReactNode {
    const { children } = this.props;
    const { focused, valid, inputAdded, testing, tested, errors } = this.state;

    const funcParams: ChildrenParams = {
      valid: focused || !inputAdded || (inputAdded && valid),
      onBlur: this.onBlur,
      onFocus: this.onFocus,
      onChange: this.onChange,
      onLoad: this.onLoad,
      testing,
      tested,
      errors,
    };

    return children(funcParams);
  }
}

export default Validate;
